import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2, Check, X, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;
// const API = 'http://localhost:8000/api';
// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = `${BACKEND_URL}/api`;

const AdvancedVoiceAssistant = ({ onNavigate, onExecuteAction, currentPage, hidden = false }) => {
    const [isListening, setIsListening] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [actions, setActions] = useState([]);
    const [currentActionIndex, setCurrentActionIndex] = useState(-1);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const startListening = async () => {
        try {
            // Request high quality audio
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    echoCancellation: true,
                    noiseSuppression: true
                }
            });

            // Check for supported MIME types - prefer mp4 for better compatibility
            let mimeType = 'audio/webm';
            let options = {};

            if (MediaRecorder.isTypeSupported('audio/mp4')) {
                mimeType = 'audio/mp4';
                options = { mimeType: 'audio/mp4' };
            } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                mimeType = 'audio/webm;codecs=opus';
                options = { mimeType: 'audio/webm;codecs=opus' };
            } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                mimeType = 'audio/webm';
                options = { mimeType: 'audio/webm' };
            } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
                mimeType = 'audio/ogg;codecs=opus';
                options = { mimeType: 'audio/ogg;codecs=opus' };
            }

            console.log('Recording with MIME type:', mimeType);

            mediaRecorderRef.current = new MediaRecorder(stream, options);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = async () => {
                console.log('Recording stopped, chunks:', audioChunksRef.current.length);
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                console.log('Audio blob size:', audioBlob.size, 'type:', audioBlob.type);
                await transcribeAudio(audioBlob, mimeType);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start(100); // Collect data every 100ms
            setIsListening(true);
            toast.info('Listening... Speak your command');
        } catch (error) {
            console.error('Error accessing microphone:', error);
            toast.error('Could not access microphone');
        }
    };

    const stopListening = () => {
        if (mediaRecorderRef.current && isListening) {
            mediaRecorderRef.current.stop();
            setIsListening(false);
        }
    };

    const transcribeAudio = async (audioBlob, mimeType) => {
        setIsProcessing(true);
        try {
            console.log('Transcribing audio blob:', audioBlob.size, 'bytes, type:', mimeType);

            const formData = new FormData();
            // Use proper file extension based on blob type
            let extension = 'webm';
            let filename = 'audio.webm';

            if (mimeType.includes('mp4')) {
                extension = 'mp4';
                filename = 'audio.mp4';
            } else if (mimeType.includes('webm')) {
                extension = 'webm';
                filename = 'audio.webm';
            } else if (mimeType.includes('ogg')) {
                extension = 'ogg';
                filename = 'audio.ogg';
            }

            formData.append('file', audioBlob, filename);

            console.log('Sending to API with filename:', filename);

            const response = await axios.post(`${API}/voice/transcribe`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            console.log('Transcription response:', response);
            const transcribedText = response.data.text;
            setTranscript(transcribedText);

            // Parse command and get actions
            await parseCommand(transcribedText);

        } catch (error) {
            console.error('Error transcribing audio:', error);
            toast.error('Failed to transcribe audio');
            setIsProcessing(false);
        }
    };

    const parseCommand = async (command) => {
        try {
            const response = await axios.post(`${API}/voice/execute-command`, {
                command,
                current_page: currentPage
            });

            if (response.data.success && response.data.actions.length > 0) {
                setActions(response.data.actions);
                await speakText(`I understood your command. I will ${response.data.actions.length} action${response.data.actions.length > 1 ? 's' : ''}.`);

                // Start executing actions
                executeActions(response.data.actions);
            } else {
                await speakText("I couldn't understand that command. Please try again.");
                setIsProcessing(false);
            }
        } catch (error) {
            console.error('Error parsing command:', error);
            toast.error('Failed to parse command');
            await speakText("Sorry, I encountered an error processing your command.");
            setIsProcessing(false);
        }
    };

    const executeActions = async (actionList) => {
        for (let i = 0; i < actionList.length; i++) {
            const action = actionList[i];
            setCurrentActionIndex(i);

            // Check if confirmation required
            if (action.requires_confirmation) {
                setPendingAction({ action, index: i });
                setShowConfirmation(true);
                await speakText(`${action.description}. Please confirm.`);
                return; // Wait for user confirmation
            }

            // Execute action
            await executeAction(action);
            await new Promise(resolve => setTimeout(resolve, 500)); // Delay between actions
        }

        // All actions completed
        setCurrentActionIndex(-1);
        setIsProcessing(false);
        await speakText("All actions completed successfully.");
        setTimeout(() => {
            setActions([]);
            setTranscript('');
        }, 3000);
    };

    const executeAction = async (action) => {
        await speakText(action.description);

        switch (action.type) {
            case 'navigate':
                if (onNavigate) {
                    onNavigate(action.params.page);
                }
                break;

            case 'compose_email':
            case 'fill_form':
            case 'click_button':
            case 'send_email':
            case 'create_booking':
            case 'create_order':
                if (onExecuteAction) {
                    await onExecuteAction(action);
                }
                break;

            default:
                console.warn('Unknown action type:', action.type);
        }
    };

    const handleConfirm = async () => {
        setShowConfirmation(false);
        if (pendingAction) {
            await executeAction(pendingAction.action);

            // Continue with remaining actions
            const remainingActions = actions.slice(pendingAction.index + 1);
            if (remainingActions.length > 0) {
                executeActions(remainingActions);
            } else {
                setCurrentActionIndex(-1);
                setIsProcessing(false);
                await speakText("Action completed.");
                setTimeout(() => {
                    setActions([]);
                    setTranscript('');
                }, 3000);
            }
        }
        setPendingAction(null);
    };

    const handleCancel = async () => {
        setShowConfirmation(false);
        setPendingAction(null);
        setActions([]);
        setCurrentActionIndex(-1);
        setIsProcessing(false);
        await speakText("Action cancelled.");
        setTimeout(() => setTranscript(''), 2000);
    };

    const speakText = async (text) => {
        try {
            const response = await axios.post(`${API}/voice/synthesize`,
                { text },
                { responseType: 'blob' }
            );

            const audioUrl = URL.createObjectURL(response.data);
            const audio = new Audio(audioUrl);

            return new Promise((resolve) => {
                audio.onended = () => {
                    URL.revokeObjectURL(audioUrl);
                    resolve();
                };
                audio.play();
            });
        } catch (error) {
            console.error('Error speaking text:', error);
        }
    };

    // Don't render if hidden (e.g., when chat panel is open)
    if (hidden) {
        return null;
    }

    return (
        <>
            {/* Floating Voice Button */}
            <motion.div
                className="fixed bottom-6 right-6 z-50"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
            >
                <button
                    onClick={isListening ? stopListening : startListening}
                    disabled={isProcessing}
                    className={`w-16 h-16 rounded-full shadow-lg flex items-center justify-center transition-all ${isListening
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                        : isProcessing
                            ? 'bg-slate-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-[#0072FF] to-[#00C6FB] hover:shadow-xl hover:scale-110'
                        }`}
                >
                    {isProcessing ? (
                        <Loader2 className="w-7 h-7 text-white animate-spin" />
                    ) : isListening ? (
                        <MicOff className="w-7 h-7 text-white" />
                    ) : (
                        <Mic className="w-7 h-7 text-white" />
                    )}
                </button>
            </motion.div>

            {/* Status Panel */}
            <AnimatePresence>
                {(transcript || actions.length > 0) && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed bottom-24 right-6 w-96 bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-4 z-50 border border-slate-200 dark:border-slate-700"
                    >
                        {/* Transcript */}
                        {transcript && (
                            <div className="mb-3">
                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">You said:</p>
                                <p className="text-sm text-slate-900 dark:text-white font-medium">{transcript}</p>
                            </div>
                        )}

                        {/* Actions List */}
                        {actions.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Actions:</p>
                                {actions.map((action, index) => (
                                    <div
                                        key={index}
                                        className={`flex items-center space-x-2 p-2 rounded-lg ${index < currentActionIndex
                                            ? 'bg-green-50 dark:bg-green-900/20'
                                            : index === currentActionIndex
                                                ? 'bg-blue-50 dark:bg-blue-900/20'
                                                : 'bg-slate-50 dark:bg-slate-700'
                                            }`}
                                    >
                                        {index < currentActionIndex ? (
                                            <Check className="w-4 h-4 text-green-600" />
                                        ) : index === currentActionIndex ? (
                                            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                        ) : (
                                            <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                                        )}
                                        <span className="text-sm text-slate-700 dark:text-slate-300">
                                            {action.description}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Confirmation Dialog */}
            <AnimatePresence>
                {showConfirmation && pendingAction && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4"
                        >
                            <div className="flex items-center space-x-3 mb-4">
                                <AlertCircle className="w-6 h-6 text-orange-500" />
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                    Confirm Action
                                </h3>
                            </div>

                            <p className="text-slate-600 dark:text-slate-300 mb-6">
                                {pendingAction.action.description}
                            </p>

                            <div className="flex space-x-3">
                                <button
                                    onClick={handleConfirm}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg hover:shadow-lg transition-all"
                                >
                                    Confirm
                                </button>
                                <button
                                    onClick={handleCancel}
                                    className="flex-1 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AdvancedVoiceAssistant;
